import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import Button from '../Utils/components/Button';
import { typographyStyles, themeColors } from '../../static/constants/styles';
import { typography } from '../../static/constants/typography';
import SomethingWentWrongSvg from '../../static/assets/illustrations/something-went-wrong.svg';
import SomethingWentWrongLightSvg from '../../static/assets/illustrations/something-went-wrong-light.svg';

interface ErrorBoundaryInnerProps {
  children: React.ReactNode;
  theme?: 'light' | 'dark';
  onReset: () => void;
  onError?: () => void;
}

interface State {
  hasError: boolean;
}

class ErrorBoundaryInner extends React.Component<ErrorBoundaryInnerProps, State> {
  constructor(props: ErrorBoundaryInnerProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch() {
    const { onError } = this.props;
    onError?.();
  }

  handleReset = () => {
    const { onReset } = this.props;
    this.setState({ hasError: false });
    onReset();
  };

  render() {
    const { hasError } = this.state;
    const { children, theme = 'dark' } = this.props;
    if (!hasError) return children;

    return (
      <Box
        sx={{
          minHeight: 'calc(100vh - 90px)',
          background: themeColors[theme].background,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            gap: 2,
            p: { xs: 4, md: 10 },
          }}
        >
          <Box
            component="img"
            src={theme === 'light' ? SomethingWentWrongLightSvg : SomethingWentWrongSvg}
            alt="Something went wrong"
            sx={{ width: { xs: 280, md: 341 } }}
          />

          <Typography
            component="h1"
            sx={{
              ...typographyStyles.h1,
              fontWeight: 700,
              fontSize: '28px',
              color: theme === 'light' ? themeColors[theme].headingColor : themeColors[theme].textSecondary,
            }}
          >
            Something went wrong.
          </Typography>

          <Typography
            sx={{
              ...typography.latoRegular18,
              color: theme === 'light' ? themeColors[theme].headingColor : themeColors[theme].textSecondary,
              maxWidth: 420,
              whiteSpace: 'nowrap',
            }}
          >
            An unexpected error occurred while loading the page.
          </Typography>

          <Button variant="navbar" onClick={this.handleReset}>
            Go Back to Home
          </Button>
        </Box>
      </Box>
    );
  }
}

const ErrorBoundary = ({
  children,
  theme,
  onError,
}: {
  children: React.ReactNode;
  theme?: 'light' | 'dark';
  onError?: () => void;
}) => {
  const navigate = useNavigate();
  return (
    <ErrorBoundaryInner theme={theme} onReset={() => navigate('/')} onError={onError}>
      {children}
    </ErrorBoundaryInner>
  );
};

export default ErrorBoundary;
